import { subject } from '@casl/ability';
import { COMMON_NAMESPACE } from '@porsche-code/kado-i18n';
import { styled } from '@porsche-code/kado-styled-system/jsx';
import { PSpinner } from '@porsche-design-system/components-react';
import { createFileRoute } from '@tanstack/react-router';
import { Trans } from 'react-i18next';

import { RouteGuard } from '@/components';
import { useAbilityContext, usePersonContext } from '@/context';
import { useDashboardEmailNotificationsQuery } from '@/graphql';

import {
  FeatureArticlePublishedNotifications,
  SupportRequestNotifications,
} from './-components';

const dashboardNotificationTopics = {
  supportRequestUpdate: 'DASHBOARD_SUPPORT_REQUEST_UPDATE',
  featureArticlePublished: 'DASHBOARD_FEATURE_ARTICLE_PUBLISHED',
} as const;

export const Route = createFileRoute('/profile/email-notifications')({
  component: () => (
    <RouteGuard can="manage" a="DashboardEmailNotification">
      <EmailNotifications />
    </RouteGuard>
  ),
  beforeLoad: () => {
    return {
      breadcrumb: () => (
        <Trans i18nKey="profile.notifications" ns={COMMON_NAMESPACE} />
      ),
    };
  },
});

function EmailNotifications() {
  const { state: ability } = useAbilityContext();
  const { state: personState } = usePersonContext();

  const { isPending: isLoadingNotifications, data: notifications } =
    useDashboardEmailNotificationsQuery(undefined, {
      refetchOnReconnect: false,
      refetchOnMount: false,
      refetchOnWindowFocus: false,
    });

  const supportRequestNotification =
    notifications?.dashboardEmailNotifications.find(
      (notification) =>
        notification.topic === dashboardNotificationTopics.supportRequestUpdate,
    );
  const featureArticlePublishedNotification =
    notifications?.dashboardEmailNotifications.find(
      (notification) =>
        notification.topic ===
        dashboardNotificationTopics.featureArticlePublished,
    );

  return (
    <>
      {isLoadingNotifications ? (
        <PSpinner />
      ) : (
        <Notifications>
          {'id' in personState &&
            typeof personState.id === 'number' &&
            ability.can(
              'manage',
              subject('DashboardEmailNotification', {
                person: personState.id,
              }),
            ) && (
              <>
                <SupportRequestNotifications
                  notification={supportRequestNotification}
                />
                <FeatureArticlePublishedNotifications
                  notification={featureArticlePublishedNotification}
                />
              </>
            )}
        </Notifications>
      )}
    </>
  );
}

const Notifications = styled('div', {
  base: {
    display: 'grid',
    rowGap: 'md',
    '& > :not(:last-child)': {
      '&:after': {
        content: '""',
        display: 'block',
        marginTop: 'md',
        borderBottomWidth: '1px',
        borderBottomStyle: 'solid',
        borderColor: 'border.contrastLow',
      },
    },
  },
});
